import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Layout from "./components/layout/Layout";
import MobileBottomTab from "./components/layout/MobileBottomTab";
import { connectSocket } from "./services/socket";

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Lazy loaded pages
const HomePage = lazy(() => import("./pages/home/HomePage"));
const GamesPage = lazy(() => import("./pages/games/GamesPage"));
const CasinoPage = lazy(() => import("./pages/casino/CasinoPage"));
const WalletPage = lazy(() => import("./pages/wallet/WalletPage"));
const TurnoverPage = lazy(() => import("./pages/turnover/TurnoverPage"));
const ProfilePage = lazy(() => import("./pages/profile/ProfilePage"));
const BonusPage = lazy(() => import("./pages/bonus/BonusPage"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const MobileMenuPage = lazy(() => import("./pages/menu/MobileMenuPage"));
const ExplorePage = lazy(() => import("./pages/explore/ExplorePage"));
const GlobalSettingsPage = lazy(() => import("./pages/settings/GlobalSettingsPage"));
const SlotsPage = lazy(() => import("./pages/slots/SlotsPage"));
const LiveCasinoPage = lazy(() => import("./pages/live/LiveCasinoPage"));
const PromotionsPage = lazy(() => import("./pages/promotions/PromotionsPage"));
const VipClubPage = lazy(() => import("./pages/vip/VipClubPage"));
const AffiliatePage = lazy(() => import("./pages/affiliate/AffiliatePage"));
const ProvablyFairPage = lazy(() => import("./pages/provably-fair/ProvablyFairPage"));
const SportsPage = lazy(() => import("./pages/sports/SportsPage"));
const VendorGamesPage = lazy(() => import("./pages/games/VendorGamesPage"));
const MobileUserPage = lazy(() => import("./pages/mobile-user/MobileUserPage"));
const NotificationPage = lazy(() => import("./pages/notification/NotificationPage"));

// Loading fallback - simple logo blink like old frontend
const PageLoader = () => (
  <div className="loading-container">
    <div className="logo-blink">
      <img src="/assets/logo.png" alt="Air1Bet"
        onError={(e) => { e.target.src = "/assets/logo.png"; }} />
    </div>
  </div>
);

// Placeholder page for routes not yet built
const PlaceholderPage = ({ title }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <h2
      className="text-2xl font-bold mb-2"
      style={{ color: "var(--color-text-bright)" }}
    >
      {title}
    </h2>
    <p style={{ color: "var(--color-text-muted)" }}>Coming soon</p>
  </div>
);

const App = () => {
  // Connect socket on app startup (like old frontend)
  useEffect(() => {
    connectSocket();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Main app routes (with layout) */}
            <Route element={<Layout />}>
              {/* Auth routes - open modal over homepage */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/register/:referralCode" element={<RegisterPage />} />
              <Route path="/referal/:referralCode" element={<RegisterPage />} />

              <Route path="/" element={<HomePage />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/menu" element={<MobileMenuPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/games/:vendor" element={<VendorGamesPage />} />
              <Route path="/providers/:vendor" element={<VendorGamesPage />} />
              <Route path="/casino" element={<CasinoPage />} />
              <Route path="/slots" element={<SlotsPage />} />
              <Route path="/pm-slots" element={<SlotsPage />} />
              <Route path="/live-casino" element={<LiveCasinoPage />} />
              <Route path="/live" element={<LiveCasinoPage />} />
              <Route path="/crash" element={<PlaceholderPage title="Crash" />} />
              <Route path="/sports" element={<SportsPage />} />
              <Route path="/bonus" element={<BonusPage />} />
              <Route path="/promotions" element={<PromotionsPage />} />
              <Route path="/vip-club" element={<VipClubPage />} />
              <Route path="/affiliate" element={<AffiliatePage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/wallet/deposit" element={<WalletPage />} />
              <Route path="/wallet/withdraw" element={<WalletPage />} />
              <Route path="/wallet/swap" element={<WalletPage />} />
              <Route path="/withdraw" element={<WalletPage />} />
              <Route path="/transactions" element={<WalletPage />} />
              <Route path="/swap" element={<WalletPage />} />
              <Route path="/turnover" element={<TurnoverPage />} />
              <Route path="/global-settings" element={<GlobalSettingsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/mobile-user" element={<MobileUserPage />} />
              <Route path="/notification" element={<NotificationPage />} />
              <Route path="/provably-fair" element={<ProvablyFairPage />} />
              <Route path="/faq" element={<PlaceholderPage title="FAQ" />} />
              <Route path="/aboutus" element={<PlaceholderPage title="About Us" />} />
              <Route path="/rules" element={<PlaceholderPage title="Rules" />} />
              <Route path="/referral/:referralCode" element={<HomePage />} />
              <Route path="*" element={<PlaceholderPage title="404 - Page Not Found" />} />
            </Route>
          </Routes>
        </Suspense>
        <MobileBottomTab />
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
